import { createContext, useContext } from "react";

/**
 * Marks a flow editor's structure as read-only — steps and connections are
 * fixed and can't be added, removed, retyped, or rewired — with an optional
 * whitelist of fields that remain editable.
 */
export interface FlowReadonly {
  readonlyStructure: boolean;
  editableFields: ReadonlySet<FlowEditorEditableField>;
  /** Outgoing handle indices that stay re-wirable despite the lock. */
  editableHandles: ReadonlySet<number>;
}

export type FlowEditorEditableField =
  | "claimMethod"
  | "conditionFalseBranch"
  | "dropoffFees"
  | "flatShippingCost"
  | "greenReturn"
  | "labelCostAdjustment"
  | "manualLabel"
  | "safetyNet";

const EDITABLE: FlowReadonly = {
  readonlyStructure: false,
  editableFields: new Set(),
  editableHandles: new Set(),
};

const FlowReadonlyContext = createContext<FlowReadonly>(EDITABLE);

export function FlowReadonlyProvider({
  readonlyStructure,
  editableFields,
  editableHandles,
  children,
}: {
  readonlyStructure: boolean;
  editableFields?: ReadonlySet<FlowEditorEditableField>;
  editableHandles?: ReadonlySet<number>;
  children: React.ReactNode;
}) {
  return (
    <FlowReadonlyContext.Provider
      value={{
        readonlyStructure,
        editableFields: editableFields ?? new Set(),
        editableHandles: editableHandles ?? new Set(),
      }}
    >
      {children}
    </FlowReadonlyContext.Provider>
  );
}

export function useFlowReadonly(): FlowReadonly {
  return useContext(FlowReadonlyContext);
}

export function useFieldEditable(
  hasPermission: boolean,
  field?: FlowEditorEditableField,
): boolean {
  const { readonlyStructure, editableFields } = useFlowReadonly();
  if (!readonlyStructure) {
    return hasPermission;
  }
  return field !== undefined && editableFields.has(field);
}
